import { useState } from "react";
import { useIntl } from "react-intl";
import { useSelector } from "react-redux";
import { Table, Popup, Icon, Dropdown } from "semantic-ui-react";
import { toast } from "../..";
import { UserRole } from "../../actions/authentificationActions";
import { downloadUnionCertificate } from "../../api/certificates";
import { TranslateProps } from "../../i18n/TranslateProps";
import {
    ApplicationStatus,
    CertificateStatus,
    CertificateType,
    certificateTypeOptions,
    getCertificateStatus,
    ICertificate,
    statePrintingCertificates,
    unionCertificates,
} from "../../models/certificate";
import { formatDate, riverRangeProps } from "../../models/common";
import { ICrewMember } from "../../models/crewMember";
import { AppState } from "../../store/configureStore";
import { CertificateFormModal } from "./CertificateForm/CertificateFormModal";
import { ModifyCertificateForm } from "./CertificateForm/ModifyCertificateForm";
import { ViewCertificateModal } from "./ViewCertificateModal";
import { PrintCertificateModal } from "./PrintCertificateModal";
import { IssuingAuthorityIndicator } from "../../components/IssuingAuthorityIndicator/IssuingAuthorityIndicator";
import { RequestFeedbackIndicator } from "../../components/RequestFeedbackIndicator/RequestFeedbackIndicator";
import { AuthorizationType, IAuthorization } from "../../models/authorization";

type Props = {
    certificate: ICertificate;
    fetchCrewMember: () => Promise<void>;
    dateOfBirth: string;
    crewMember: ICrewMember;
    renewable: boolean;
    authorizations: IAuthorization[];
};

export const CertificatesRow = ({
    certificate,
    fetchCrewMember,
    dateOfBirth,
    crewMember,
    renewable,
    authorizations,
}: Props) => {
    const user = useSelector((state: AppState) => state.user);
    const intl = useIntl();
    const [modifyModalOpen, setModifyModalOpen] = useState(false);
    const [editModalOpen, setEditModalOpen] = useState(false);
    const [renewModalOpen, setRenewModalOpen] = useState(false);
    const [viewModalOpen, setViewModalOpen] = useState(false);
    const [printModalOpen, setPrintModalOpen] = useState(false);
    const [loading, setLoading] = useState(false);

    const getFilename = () => {
        switch (certificate.type) {
            case CertificateType.UcqBoatmaster:
                return "UCQBoatmaster_" + crewMember.cid + ".pdf";
            case CertificateType.UcqLngExpert:
                return "UCQLNGExpert_" + crewMember.cid + ".pdf";
            case CertificateType.UcqPassengerNavigationExpert:
                return "UCQPassengerNavigationExpert_" + crewMember.cid + ".pdf";
            default:
                return "";
        }
    };

    const handleDownloadUnionCertificate = async () => {
        setLoading(true);
        try {
            await downloadUnionCertificate(certificate.qualificationID, getFilename());
        } catch {
            toast(intl.formatMessage({ id: "failedToDownload" }), false);
        } finally {
            setLoading(false);
        }
    };

    const formatRanges = () => {
        let stretches: string[] = [];
        if (certificate.type === CertificateType.UcqBoatmaster) {
            // get all enum stretches from authorizations
            stretches = authorizations
                .filter(a => a.linkedQualificationID === certificate.qualificationID)
                .filter(a => a.type === AuthorizationType.S)
                .filter(a => a.riverKilometers.length > 0)
                .reduce((urk, auth) => {
                    auth.riverKilometers.forEach(s => {
                        urk.push(
                            `${
                                riverRangeProps.find(r => r.key === s)?.text?.toString() ?? ""
                            } - ${auth.qualificationID.slice(-7)}`
                        );
                    });
                    return urk;
                }, [] as string[])
                .sort((a, b) => a.localeCompare(b));
        } else if (
            certificate.type ===
            CertificateType.CertificateForSailingOnStretchesOfWaterwaysThatPresentSpecificRisksForNavigationAt
        ) {
            // get all stretches in string array, from certificate as text in riverRangeProps
            stretches = (
                certificate.riverKilometers.map(
                    r => riverRangeProps.find(x => x.value === r)?.text ?? ""
                ) as string[]
            ).sort((a, b) => a.localeCompare(b));
        }

        // show multiple stretches with info icon, show single stretch in line, show slash if no stretches
        if (stretches.length > 1) {
            return (
                <span>
                    {intl.formatMessage({ id: "multiple" })}
                    <Popup
                        content={stretches.map(r => (
                            <p key={r}>{r}</p>
                        ))}
                        position="top center"
                        flowing
                        trigger={<Icon name="info circle" size="large" />}
                        hoverable
                    />
                </span>
            );
        } else if (stretches.length === 1) {
            return <span>{stretches[0]}</span>;
        } else {
            return "/";
        }
    };

    return (
        <>
            <Table.Row>
                <Table.Cell width={1}>
                    <IssuingAuthorityIndicator issuingAuthority={certificate.issuingAuthority} />
                </Table.Cell>
                <Table.Cell>
                    {
                        TranslateProps(certificateTypeOptions).find(
                            x => x.value === certificate.type
                        )?.text
                    }
                </Table.Cell>
                <Table.Cell>{formatRanges()}</Table.Cell>
                <Table.Cell>{certificate.qualificationID}</Table.Cell>
                <Table.Cell>
                    {certificate.endDate.includes("0001-") ? "/" : formatDate(certificate.endDate)}
                </Table.Cell>
                <Table.Cell width={2}>{getCertificateStatus(certificate)}</Table.Cell>
                <Table.Cell collapsing>
                    <span className="buttons-container">
                        <Dropdown
                            id="certificates-options"
                            floating
                            text={intl.formatMessage({ id: "options" })}
                            loading={loading}
                        >
                            <Dropdown.Menu>
                                <Dropdown.Item
                                    text={intl.formatMessage({ id: "view" })}
                                    onClick={() => setViewModalOpen(true)}
                                />
                                {user.role === UserRole.EnforcementAuthorityUser &&
                                (certificate.status === CertificateStatus.Active ||
                                    certificate.status === CertificateStatus.Suspended) ? (
                                    <Dropdown.Item
                                        text={intl.formatMessage({ id: "suspend" })}
                                        onClick={() => setModifyModalOpen(true)}
                                    />
                                ) : (
                                    user.role !== UserRole.EnforcementAuthorityUser &&
                                    (certificate.type !==
                                        CertificateType.InternationalCertificateForOperatorsOfPleasureCraftICS ||
                                        certificate.applicationStatus ===
                                            ApplicationStatus.Completed) && (
                                        <Dropdown.Item
                                            text={intl.formatMessage({ id: "changeStatus" })}
                                            onClick={() => setModifyModalOpen(true)}
                                        />
                                    )
                                )}
                                {user.role !== UserRole.EnforcementAuthorityUser &&
                                    certificate.type !==
                                        CertificateType.InternationalCertificateForOperatorsOfPleasureCraftICS && (
                                        <Dropdown.Item
                                            text={intl.formatMessage({ id: "renew" })}
                                            onClick={() => setRenewModalOpen(true)}
                                            disabled={
                                                !renewable ||
                                                certificate.issuingAuthority !== user.authority
                                            }
                                        />
                                    )}
                                {certificate.type ===
                                    CertificateType.InternationalCertificateForOperatorsOfPleasureCraftICS && (
                                    <Dropdown.Item
                                        text={intl.formatMessage({ id: "viewEditDetails" })}
                                        onClick={() => setEditModalOpen(true)}
                                    />
                                )}
                                {unionCertificates.includes(certificate.type) && !crewMember.cid ? (
                                    <Popup
                                        content={intl.formatMessage({ id: "cidNotAssigned" })}
                                        position="top center"
                                        trigger={
                                            <Dropdown.Item
                                                id="disabled-button"
                                                text={intl.formatMessage({ id: "print" })}
                                            />
                                        }
                                    />
                                ) : (
                                    <Dropdown.Item
                                        text={intl.formatMessage({ id: "print" })}
                                        disabled={
                                            user.role === UserRole.EnforcementAuthorityUser ||
                                            !statePrintingCertificates.includes(certificate.type) ||
                                            loading ||
                                            (certificate.type ===
                                                CertificateType.InternationalCertificateForOperatorsOfPleasureCraftICS &&
                                                ((certificate.printAcknowledged &&
                                                    !certificate.printFeedback) ||
                                                    certificate.canceled)) ||
                                            (certificate.type !==
                                                CertificateType.InternationalCertificateForOperatorsOfPleasureCraftICS &&
                                                certificate.issuingAuthority !== user.authority)
                                        }
                                        onClick={() => setPrintModalOpen(true)}
                                    />
                                )}
                                {unionCertificates.includes(certificate.type) && !crewMember.cid ? (
                                    <Popup
                                        content={intl.formatMessage({ id: "cidNotAssigned" })}
                                        position="top center"
                                        trigger={
                                            <Dropdown.Item
                                                text={`${intl.formatMessage({
                                                    id: "download",
                                                })} (${intl.formatMessage({ id: "certificate" })})`}
                                                disabled={
                                                    user.role ===
                                                        UserRole.EnforcementAuthorityUser ||
                                                    ![
                                                        CertificateType.UcqBoatmaster,
                                                        CertificateType.UcqLngExpert,
                                                        CertificateType.UcqPassengerNavigationExpert,
                                                    ].includes(certificate.type) ||
                                                    loading ||
                                                    certificate.issuingAuthority !== user.authority
                                                }
                                            />
                                        }
                                    />
                                ) : (
                                    certificate.type !==
                                        CertificateType.InternationalCertificateForOperatorsOfPleasureCraftICS && (
                                        <Dropdown.Item
                                            text={`${intl.formatMessage({
                                                id: "download",
                                            })} (${intl.formatMessage({ id: "certificate" })})`}
                                            disabled={
                                                user.role === UserRole.EnforcementAuthorityUser ||
                                                ![
                                                    CertificateType.UcqBoatmaster,
                                                    CertificateType.UcqLngExpert,
                                                    CertificateType.UcqPassengerNavigationExpert,
                                                ].includes(certificate.type) ||
                                                loading ||
                                                certificate.issuingAuthority !== user.authority
                                            }
                                            onClick={() => handleDownloadUnionCertificate()}
                                        />
                                    )
                                )}
                            </Dropdown.Menu>
                        </Dropdown>
                        {certificate.type ===
                        CertificateType.InternationalCertificateForOperatorsOfPleasureCraftICS ? null : certificate
                              .requestFeedbacks.length > 0 ? (
                            <RequestFeedbackIndicator feedbacks={certificate.requestFeedbacks} />
                        ) : certificate.ecdbAcknowledged ? (
                            <Icon name="check circle" size="big" />
                        ) : null}
                    </span>
                </Table.Cell>
            </Table.Row>
            {renewModalOpen && (
                <CertificateFormModal
                    open={renewModalOpen}
                    close={() => setRenewModalOpen(false)}
                    fetchCrewMember={fetchCrewMember}
                    dateOfBirth={dateOfBirth}
                    certificates={[]}
                    certificate={certificate}
                    crewMember={crewMember}
                />
            )}
            {editModalOpen && (
                <CertificateFormModal
                    open={editModalOpen}
                    close={() => setEditModalOpen(false)}
                    fetchCrewMember={fetchCrewMember}
                    dateOfBirth={dateOfBirth}
                    certificates={[]}
                    certificate={certificate}
                    crewMember={crewMember}
                    isUpdate
                />
            )}
            {modifyModalOpen && (
                <ModifyCertificateForm
                    open={modifyModalOpen}
                    close={() => setModifyModalOpen(false)}
                    certificate={certificate}
                    fetchCrewMember={fetchCrewMember}
                />
            )}
            {viewModalOpen && (
                <ViewCertificateModal
                    open={viewModalOpen}
                    close={() => setViewModalOpen(false)}
                    certificate={certificate}
                    idDataSet={crewMember.idDataSets[0]}
                    qrBase64={crewMember.qrBase64}
                />
            )}
            {printModalOpen && (
                <PrintCertificateModal
                    open={printModalOpen}
                    closeModal={() => setPrintModalOpen(false)}
                    certificate={certificate}
                />
            )}
        </>
    );
};
